/**
 * MobileCompanyProfileFlow
 * Unified mobile DNA flow aligned with DesktopCompanyDNAPage.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import './MobileCompanyProfile.css';
import useMobileCompanyProfile from '../../../hooks/useMobileCompanyProfile.js';
import useLanguage from '../../../hooks/useLanguage';
import useApi from '../../../hooks/useApi.js';
import { useMobileAlert } from '../common/alerts/MobileAlertContext';
import { API_CONFIG } from '../../../config/api';
import { migrateLegacyMediaUrlsInLocalStorage, normalizeMediaUrl } from '../../../utils/normalizeMediaUrl';

import iconAi from '../../../assets/images/icons/icon-ai.svg';
import iconWebsite from '../../../assets/images/icons/icon-website.svg';
import iconUpload from '../../../assets/images/icons/icon-upload.svg';
import iconDelete from '../../../assets/images/icons/icon-delete.svg';
import iconImage from '../../../assets/images/icons/icon-image.svg';
import iconPdf from '../../../assets/images/icons/icon-pdf.svg';
import iconSave from '../../../assets/images/icons/icon-save.svg';

const COMPANY_SIZE_OPTION_DEFINITIONS = [
  { value: '1-10', key: '1_10' },
  { value: '11-50', key: '11_50' },
  { value: '51-200', key: '51_200' },
  { value: '201-500', key: '201_500' },
  { value: '501-1000', key: '501_1000' },
  { value: '1001-5000', key: '1001_5000' },
  { value: '5000+', key: '5000_plus' },
  { value: 'Unknown', key: 'unknown' }
];

const INDUSTRY_OPTION_DEFINITIONS = [
  { value: 'Tecnologia', key: 'technology' },
  { value: 'Healthcare', key: 'healthcare' },
  { value: 'Finance', key: 'finance' },
  { value: 'E-commerce', key: 'ecommerce' },
  { value: 'Educazione', key: 'education' },
  { value: 'Food & Beverage', key: 'food' },
  { value: 'Fashion', key: 'fashion' },
  { value: 'Real Estate', key: 'realEstate' },
  { value: 'Consulting', key: 'consulting' },
  { value: 'Marketing', key: 'marketing' },
  { value: 'Altro', key: 'other' }
];

const BRAND_PERSONALITY_OPTION_DEFINITIONS = [
  { value: 'Innovativa', key: 'innovative' },
  { value: 'Affidabile', key: 'reliable' },
  { value: 'Friendly', key: 'friendly' },
  { value: 'Premium', key: 'premium' },
  { value: 'Giovanile', key: 'youthful' },
  { value: 'Professionale', key: 'professional' },
  { value: 'Creativa', key: 'creative' },
  { value: 'Sostenibile', key: 'sustainable' },
  { value: 'Lusso', key: 'luxury' },
  { value: 'Accessibile', key: 'accessible' }
];

const DnaConfirmModal = ({ isOpen, onConfirm, onCancel, isGenerating, translations }) => {
  if (!isOpen) return null;

  return (
    <div className="mobile-info-modal-overlay" onClick={onCancel}>
      <div className="mobile-info-modal mobile-dna-confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mobile-info-modal-header">
          <h3>{translations.title}</h3>
          <button className="mobile-info-modal-close" onClick={onCancel}>x</button>
        </div>
        <div className="mobile-info-modal-content">
          <div className="mobile-dna-confirm-lead">
            {translations.lead}
          </div>
          <div className="mobile-dna-confirm-body">
            {translations.body}
          </div>
        </div>
        <div className="mobile-dna-confirm-actions">
          <button
            className="mobile-dna-confirm-button cancel"
            onClick={onCancel}
            disabled={isGenerating}
            type="button"
          >
            {translations.cancel}
          </button>
          <button
            className="mobile-dna-confirm-button confirm"
            onClick={onConfirm}
            disabled={isGenerating}
            type="button"
          >
            {isGenerating ? translations.generating : translations.confirm}
          </button>
        </div>
      </div>
    </div>
  );
};

const Stepper = ({ steps, activeStep }) => {
  const active = steps.find((step) => step.step === activeStep) || steps[0];
  return (
    <div className="mobile-dna-stepper">
      <div className="mobile-dna-stepper-header">
        <p className="mobile-dna-stepper-title">{active?.title}</p>
        <p className="mobile-dna-stepper-desc">{active?.description}</p>
      </div>
      <div className="mobile-dna-stepper-track" role="list">
        {steps.map((step, index) => {
          const state = activeStep > step.step ? 'complete' : activeStep === step.step ? 'active' : 'inactive';
          return (
            <React.Fragment key={step.step}>
              <div className={`mobile-dna-step-dot ${state}`} role="listitem">
                <div className="mobile-dna-step-indicator">{step.step}</div>
              </div>
              {index < steps.length - 1 && (
                <div className={`mobile-dna-step-separator ${activeStep > step.step ? 'active' : ''}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

const LabeledField = ({ label, hint, children }) => (
  <label className="mobile-dna-field">
    <div className="mobile-dna-field-meta">
      <span>{label}</span>
      {hint && <small>{hint}</small>}
    </div>
    {children}
  </label>
);

const DropdownSelect = ({ value, placeholder, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => option.value === value);
  const display = selected ? selected.label : placeholder;

  return (
    <div className="mobile-dna-dropdown" data-open={open || undefined}>
      <button
        type="button"
        className="mobile-dna-dropdown-trigger"
        onClick={() => setOpen((prev) => !prev)}
      >
        <span>{display}</span>
        <svg className={`mobile-dna-dropdown-icon ${open ? 'open' : ''}`} width="12" height="8" viewBox="0 0 12 8" fill="none">
          <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="mobile-dna-dropdown-menu">
          {options.map((option) => (
            <button
              type="button"
              key={option.value}
              className={`mobile-dna-dropdown-item ${value === option.value ? 'active' : ''}`}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const MobileCompanyProfileFlow = ({ companyId = null }) => {
  const api = useApi();
  const { t } = useLanguage();
  const { notify } = useMobileAlert();
  const mobileProfile = useMobileCompanyProfile(companyId);

  const {
    profileData,
    updateProfileField,
    handleFileUpload,
    removeFile,
    addLink,
    saveProfile,
    performAiAnalysis,
    generateAiDna,
    isMobileProcessing,
    currentCompany,
    currentStep,
    setCurrentStep,
    aiAnalysisComplete,
    setAiAnalysisComplete
  } = mobileProfile;

  const uploadedDocuments = profileData.uploadedDocuments || [];
  const uploadedImages = profileData.uploadedImages || [];
  const uploadedLinks = profileData.uploadedLinks || [];

  const [localStorageLogo, setLocalStorageLogo] = useState(null);
  const [logoObjectFit] = useState(() => {
    const saved = localStorage.getItem('companyLogoObjectFit');
    return saved || 'cover';
  });

  const [linkTextarea, setLinkTextarea] = useState('');
  const [linkError, setLinkError] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [manualGenerating, setManualGenerating] = useState(false);
  const [validationLoading, setValidationLoading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [activeStep, setActiveStep] = useState(1);
  const [isDnaComplete, setIsDnaComplete] = useState(false);
  const [showDnaConfirmModal, setShowDnaConfirmModal] = useState(false);
  const [dnaMetadata, setDnaMetadata] = useState(null);
  const [dnaLoading, setDnaLoading] = useState(false);

  const documentInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const fetchedDnaRef = useRef(false);

  const companyLogo = profileData.logo || localStorageLogo || null;
  const companyWebsite = profileData.website || '';

  const normalizedCompanyLogo = useMemo(() => {
    if (!companyLogo) return null;
    const preview = normalizeMediaUrl(companyLogo.preview || companyLogo.url, API_CONFIG.BASE_URL);
    const url = normalizeMediaUrl(companyLogo.url || companyLogo.preview, API_CONFIG.BASE_URL);
    return {
      ...companyLogo,
      preview,
      url
    };
  }, [companyLogo]);

  const activeCompanyId = useMemo(
    () => companyId || currentCompany?.id || profileData?.id || null,
    [companyId, currentCompany?.id, profileData?.id]
  );

  const hasExistingDna = useMemo(() => {
    const generated = profileData?.generated_dna;
    const legacy = profileData?.dna;
    if (typeof generated === 'string') return generated.trim().length > 0;
    if (typeof legacy === 'string') return legacy.trim().length > 0;
    return Boolean(generated || legacy);
  }, [profileData?.dna, profileData?.generated_dna]);

  const stepperSteps = useMemo(() => ([
    {
      step: 1,
      title: t('companyProfile.dna.steps.auto', { defaultValue: 'Compilazione automatica DNA con AI' }),
      description: t('companyProfile.dna.steps.autoDesc', { defaultValue: 'Carica materiali e avvia analisi' })
    },
    {
      step: 2,
      title: t('companyProfile.dna.steps.profile', { defaultValue: 'Profilo DNA' }),
      description: t('companyProfile.dna.steps.profileDesc', { defaultValue: 'Rivedi e modifica i campi compilati' })
    },
    {
      step: 3,
      title: t('companyProfile.dna.steps.complete', { defaultValue: 'DNA completo' }),
      description: t('companyProfile.dna.steps.completeDesc', { defaultValue: 'Riepilogo finale non modificabile' })
    }
  ]), [t]);

  const companySizeOptions = useMemo(
    () => COMPANY_SIZE_OPTION_DEFINITIONS.map(({ value, key }) => ({
      value,
      label: t(`companyProfile.manual.dropdowns.sizeOptions.${key}`)
    })),
    [t]
  );

  const industryOptions = useMemo(
    () => INDUSTRY_OPTION_DEFINITIONS.map(({ value, key }) => ({
      value,
      label: t(`companyProfile.manual.dropdowns.industryOptions.${key}`)
    })),
    [t]
  );

  const brandPersonalityOptions = useMemo(
    () => BRAND_PERSONALITY_OPTION_DEFINITIONS.map(({ value, key }) => ({
      value,
      label: t(`companyProfile.manual.dropdowns.personalityOptions.${key}`)
    })),
    [t]
  );

  const dnaConfirmTranslations = useMemo(() => ({
    title: t('companyProfile.manual.dnaConfirm.title'),
    lead: (
      <Trans i18nKey="companyProfile.manual.dnaConfirm.lead" components={{ strong: <strong /> }} />
    ),
    body: (
      <Trans i18nKey="companyProfile.manual.dnaConfirm.body" components={{ strong: <strong /> }} />
    ),
    cancel: t('companyProfile.common.cancel'),
    confirm: t('companyProfile.manual.dnaConfirm.confirm'),
    generating: t('companyProfile.manual.buttons.generating')
  }), [t]);

  useEffect(() => {
    const loadLogoFromLocalStorage = () => {
      migrateLegacyMediaUrlsInLocalStorage(
        ['companyLogoPreview', 'companyLogoRemoteUrl'],
        API_CONFIG.BASE_URL
      );
      const logoPreview = normalizeMediaUrl(localStorage.getItem('companyLogoPreview'), API_CONFIG.BASE_URL);
      const logoName = localStorage.getItem('companyLogoName');
      const storedCompanyId = localStorage.getItem('companyLogoCompanyId');

      if (logoPreview && logoName) {
        setLocalStorageLogo({
          preview: logoPreview,
          name: logoName,
          companyId: storedCompanyId || null,
          url: logoPreview
        });
      } else {
        setLocalStorageLogo(null);
      }
    };

    loadLogoFromLocalStorage();

    const handleLogoChange = (event) => {
      const { preview, name, companyId: eventCompanyId } = event.detail || {};
      const normalizedPreview = normalizeMediaUrl(preview, API_CONFIG.BASE_URL);
      if (normalizedPreview && name) {
        setLocalStorageLogo({
          preview: normalizedPreview,
          name,
          companyId: eventCompanyId || null,
          url: normalizedPreview
        });
      } else {
        setLocalStorageLogo(null);
      }
    };

    window.addEventListener('companyLogoChanged', handleLogoChange);
    return () => {
      window.removeEventListener('companyLogoChanged', handleLogoChange);
    };
  }, []);

  useEffect(() => {
    const resolvedCompanyId = companyId || currentCompany?.id || null;
    if (profileData.logo && profileData.logo.url && profileData.logo.existing) {
      const normalizedLogoUrl = normalizeMediaUrl(profileData.logo.url, API_CONFIG.BASE_URL);
      localStorage.setItem('companyLogoPreview', normalizedLogoUrl);
      localStorage.setItem('companyLogoName', profileData.logo.name);
      if (resolvedCompanyId) {
        localStorage.setItem('companyLogoCompanyId', resolvedCompanyId);
      } else {
        localStorage.removeItem('companyLogoCompanyId');
      }

      window.dispatchEvent(new CustomEvent('companyLogoChanged', {
        detail: {
          preview: normalizedLogoUrl,
          name: profileData.logo.name,
          companyId: resolvedCompanyId,
          remoteUrl: normalizedLogoUrl
        }
      }));
    } else if (!profileData.logo || (!profileData.logo.url && !profileData.logo.preview)) {
      localStorage.removeItem('companyLogoPreview');
      localStorage.removeItem('companyLogoName');
      localStorage.removeItem('companyLogoCompanyId');
      localStorage.removeItem('companyLogoRemoteUrl');
      localStorage.removeItem('companyLogoPreviewUpdatedAt');

      window.dispatchEvent(new CustomEvent('companyLogoChanged', {
        detail: {
          preview: null,
          name: null,
          companyId: resolvedCompanyId
        }
      }));
    }
  }, [companyId, currentCompany?.id, profileData.logo]);

  useEffect(() => {
    if (isDnaComplete) {
      setActiveStep(3);
      return;
    }

    if (aiAnalysisComplete || currentStep === 2) {
      setActiveStep(2);
    } else {
      setActiveStep(1);
    }
  }, [aiAnalysisComplete, currentStep, isDnaComplete]);

  useEffect(() => {
    if (hasExistingDna) {
      setIsDnaComplete(true);
      setActiveStep(3);
      setAiAnalysisComplete(true);
      setCurrentStep?.(3);
    } else if (!hasExistingDna && isDnaComplete) {
      setIsDnaComplete(false);
    }
  }, [hasExistingDna, profileData.dna, profileData.generated_dna, setAiAnalysisComplete, setCurrentStep, isDnaComplete]);

  const formatDnaText = useCallback((dnaPayload) => {
    if (!dnaPayload) return '';
    if (typeof dnaPayload === 'string') return dnaPayload;
    try {
      if (dnaPayload.dna) {
        return typeof dnaPayload.dna === 'string'
          ? dnaPayload.dna
          : JSON.stringify(dnaPayload.dna, null, 2);
      }
      return JSON.stringify(dnaPayload, null, 2);
    } catch {
      return '';
    }
  }, []);

  const fetchExistingDna = useCallback(async () => {
    if (!activeCompanyId || fetchedDnaRef.current || profileData.generated_dna) return;

    setDnaLoading(true);
    try {
      const response = await api.get(`/api/v1/companies/${activeCompanyId}/dna`);
      const dnaText = formatDnaText(response?.dna);
      updateProfileField('generated_dna', dnaText);
      setDnaMetadata({
        metadata: response?.metadata || response?.analysis_metadata || response?.dna?.analysis_metadata,
        completeness: response?.completeness_analysis
      });
      fetchedDnaRef.current = true;
    } catch (error) {
      console.error('[MobileCompanyProfileFlow] Fetch DNA error', error);
    } finally {
      setDnaLoading(false);
    }
  }, [activeCompanyId, api, formatDnaText, profileData.generated_dna, updateProfileField]);

  const isSummaryStep = isDnaComplete || activeStep === 3;
  const isProfileStep = !isSummaryStep && activeStep === 2;
  const isAutoStep = !isSummaryStep && activeStep === 1;

  useEffect(() => {
    if (isSummaryStep && !profileData.generated_dna) {
      fetchExistingDna();
    }
  }, [fetchExistingDna, isSummaryStep, profileData.generated_dna]);

  const handleAddLinks = useCallback(() => {
    const url = linkTextarea.trim();
    if (!url) {
      setLinkError('');
      return;
    }

    try {
      const parsed = new URL(url);
      const description = parsed.hostname.replace(/^www\./, '');
      addLink({
        id: Date.now(),
        url,
        description
      });
    } catch (error) {
      setLinkError(`Link non valido: ${url}`);
      return;
    }

    setLinkError('');
    setLinkTextarea('');
  }, [addLink, linkTextarea, t]);

  const handleDocumentUpload = (event) => {
    const files = Array.from(event.target.files);
    handleFileUpload(files, 'documents');
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    handleFileUpload(files, 'images');
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    handleFileUpload([file], 'logo');

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      const resolvedCompanyId = companyId || currentCompany?.id || null;
      const updatedAt = Date.now();

      localStorage.setItem('companyLogoPreview', dataUrl);
      localStorage.setItem('companyLogoName', file.name);
      localStorage.setItem('companyLogoPreviewUpdatedAt', String(updatedAt));
      if (resolvedCompanyId) {
        localStorage.setItem('companyLogoCompanyId', resolvedCompanyId);
      } else {
        localStorage.removeItem('companyLogoCompanyId');
      }

      window.dispatchEvent(new CustomEvent('companyLogoChanged', {
        detail: { preview: dataUrl, name: file.name, companyId: resolvedCompanyId, updatedAt }
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    removeFile('logo', null);
    localStorage.removeItem('companyLogoPreview');
    localStorage.removeItem('companyLogoName');
    localStorage.removeItem('companyLogoCompanyId');
    localStorage.removeItem('companyLogoRemoteUrl');
    localStorage.removeItem('companyLogoPreviewUpdatedAt');

    window.dispatchEvent(new CustomEvent('companyLogoChanged', {
      detail: { preview: null, name: null, companyId: companyId || currentCompany?.id || null }
    }));
  };

  const handleGenerateDNA = useCallback(async () => {
    if (!activeCompanyId) {
      notify({
        variant: 'warning',
        title: t('companyProfile.manual.alerts.noCompany')
      });
      return;
    }

    if (!companyWebsite && uploadedDocuments.length === 0 && uploadedImages.length === 0) {
      notify({
        variant: 'warning',
        title: t('companyProfile.ai.alerts.missingInputs')
      });
      return;
    }

    setIsDnaComplete(false);
    setAiAnalysisComplete(false);
    setCurrentStep?.(1);
    setActiveStep(1);
    setIsGenerating(true);
    setValidationResult(null);
    setValidationError('');

    try {
      await performAiAnalysis(companyWebsite, uploadedDocuments, uploadedImages);
      notify({
        variant: 'success',
        title: t('companyProfile.manual.alerts.dnaSuccess')
      });
    } catch (error) {
      console.error('[MobileCompanyProfileFlow] AI analysis failed', error);
      try {
        await generateAiDna();
        notify({
          variant: 'warning',
          title: t('companyProfile.manual.alerts.dnaFallback', { defaultValue: 'Creato DNA dai dati inseriti' })
        });
      } catch (fallbackError) {
        console.error('[MobileCompanyProfileFlow] Unable to generate DNA', fallbackError);
        const errorMessage = fallbackError?.message || error?.message || t('companyProfile.manual.alerts.unknownError', {
          defaultValue: 'Errore sconosciuto'
        });
        notify({
          variant: 'error',
          title: t('companyProfile.manual.alerts.dnaError', { message: errorMessage })
        });
      }
    } finally {
      setIsGenerating(false);
    }
  }, [activeCompanyId, companyWebsite, generateAiDna, notify, performAiAnalysis, setAiAnalysisComplete, setCurrentStep, t, uploadedDocuments, uploadedImages]);

  const handleSaveProfile = useCallback(async () => {
    try {
      await saveProfile();
      notify({
        variant: 'success',
        title: t('companyProfile.manual.alerts.saveSuccess')
      });
    } catch (error) {
      console.error('[MobileCompanyProfileFlow] Save error', error);
      notify({
        variant: 'error',
        title: error?.message || t('companyProfile.manual.alerts.saveGenericError')
      });
    }
  }, [notify, saveProfile, t]);

  const buildManualInput = useCallback(() => {
    const links = (uploadedLinks || []).map((link) => link.url).filter(Boolean);
    return {
      company_name: profileData.name || '',
      company_email: profileData.email || '',
      company_phone: profileData.phone || '',
      company_website: profileData.website || '',
      company_size: profileData.size || '',
      founded_year: profileData.founded_year || '',
      company_address: profileData.address || '',
      industry: profileData.industry || '',
      company_description: profileData.description || '',
      brand_personality: profileData.brand_personality || '',
      competitive_advantage: profileData.competitive_advantage || '',
      target_audience_description: profileData.target_audience || '',
      brand_values: profileData.brand_values || '',
      communication_style: profileData.communication_style || '',
      mission_vision: profileData.mission_vision || '',
      key_products_services: profileData.key_products || '',
      company_history: profileData.company_history || '',
      useful_links: links
    };
  }, [profileData, uploadedLinks]);

  const handleValidateManual = useCallback(async () => {
    if (!activeCompanyId) {
      notify({
        variant: 'warning',
        title: t('companyProfile.manual.alerts.noCompany')
      });
      return;
    }

    setValidationLoading(true);
    setValidationError('');
    setValidationResult(null);

    try {
      const manual_input = buildManualInput();
      const response = await api.post(`/api/v1/companies/${activeCompanyId}/dna/validate-input`, { manual_input });
      setValidationResult(response.validation_result || null);
      notify({
        variant: 'success',
        title: response?.message || t('companyProfile.manual.alerts.validationSuccess', { defaultValue: 'Validazione completata' })
      });
    } catch (error) {
      console.error('[MobileCompanyProfileFlow] Manual input validation error', error);
      const errorMessage = error?.message || t('companyProfile.manual.alerts.validationErrors');
      setValidationError(errorMessage);
      notify({
        variant: 'error',
        title: errorMessage
      });
    } finally {
      setValidationLoading(false);
    }
  }, [activeCompanyId, api, buildManualInput, notify, t]);

  const handleGenerateManualDna = useCallback(async () => {
    if (!activeCompanyId) {
      notify({
        variant: 'warning',
        title: t('companyProfile.manual.alerts.noCompany')
      });
      return;
    }

    setManualGenerating(true);
    try {
      const manual_input = buildManualInput();
      const payload = {
        generation_type: 'manual',
        manual_input,
        custom_instructions: t('companyProfile.manual.analysis.instructions')
      };

      const response = await api.post(`/api/v1/companies/${activeCompanyId}/dna/manual`, payload);
      const dnaText = formatDnaText(response?.dna);
      setDnaMetadata({
        completeness: response?.completeness_analysis,
        metadata: response?.metadata || response?.analysis_metadata
      });
      updateProfileField('generated_dna', dnaText);
      setAiAnalysisComplete(true);
      setIsDnaComplete(true);
      setCurrentStep?.(3);
      setActiveStep(3);

      notify({
        variant: 'success',
        title: response?.message || t('companyProfile.manual.alerts.dnaComplete', { defaultValue: 'DNA completo generato' })
      });
    } catch (error) {
      console.error('[MobileCompanyProfileFlow] Manual DNA generation error', error);
      const errorMessage = error?.message || t('companyProfile.manual.alerts.unknownError', {
        defaultValue: 'Errore sconosciuto'
      });
      notify({
        variant: 'error',
        title: t('companyProfile.manual.alerts.dnaError', { message: errorMessage })
      });
    } finally {
      setManualGenerating(false);
    }
  }, [activeCompanyId, api, buildManualInput, formatDnaText, notify, setCurrentStep, setAiAnalysisComplete, t, updateProfileField]);

  return (
    <div className="mobile-new-campaign-page mobile-dna-shell">
      <div className="mobile-new-campaign-content mobile-dna-content">
        <div className="generation-page-header">
          <div className="mobile-ai-storage-header">
            <h1 className="mobile-ai-storage-title">{t('companyProfile.manual.header', { defaultValue: 'DNA Aziendale' })}</h1>
            <p className="mobile-ai-storage-subtitle">
              {t('companyProfile.dna.heroSubtitle', {
                defaultValue: 'Raccogli identita e materiali essenziali per generare un DNA pronto per i team.'
              })}
            </p>
          </div>
        </div>

        {isProfileStep && (
          <div className="mobile-dna-actions">
            <button
              type="button"
              className="mobile-dna-button surface"
              onClick={handleSaveProfile}
              disabled={isMobileProcessing}
            >
              <img src={iconSave} alt="" />
              {t('companyProfile.common.saveAlt', { defaultValue: 'Salva' })}
            </button>
          </div>
        )}

        <Stepper steps={stepperSteps} activeStep={activeStep} />

        {isAutoStep && (
          <section className="mobile-dna-card">
            <div className="mobile-dna-card-header">
              <p className="mobile-dna-eyebrow">{t('companyProfile.ai.autoEyebrow', { defaultValue: 'Compilazione AI' })}</p>
              <h2 className="mobile-dna-card-title">{t('companyProfile.ai.autoTitle', { defaultValue: 'DNA automatico' })}</h2>
              <p className="mobile-dna-card-description">{t('companyProfile.ai.subtitle')}</p>
            </div>

            <div className="mobile-dna-auto-grid">
              <div className="mobile-dna-upload-card">
                <div className="mobile-dna-upload-head">
                  <div className="mobile-dna-upload-title">
                    <img src={iconImage} alt="" />
                    <span>{t('companyProfile.manual.logoSection.title')}</span>
                  </div>
                </div>
                <div className="mobile-dna-logo-slot">
                  {normalizedCompanyLogo ? (
                    <>
                      <img
                        src={normalizedCompanyLogo.preview || normalizedCompanyLogo.url}
                        alt={t('companyProfile.manual.logoSection.alt')}
                        className="mobile-dna-logo-preview"
                        style={{ objectFit: logoObjectFit }}
                      />
                      <button
                        type="button"
                        className="mobile-dna-icon-button danger"
                        onClick={handleRemoveLogo}
                        aria-label={t('companyProfile.common.removeAlt')}
                      >
                        <img src={iconDelete} alt="" />
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="mobile-dna-upload-trigger"
                      onClick={() => logoInputRef.current?.click()}
                    >
                      <img src={iconUpload} alt="" />
                      <span>{t('companyProfile.manual.logoSection.placeholder')}</span>
                    </button>
                  )}
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>

              <div className="mobile-dna-upload-card">
                <div className="mobile-dna-upload-head">
                  <div className="mobile-dna-upload-title">
                    <img src={iconImage} alt="" />
                    <span>{t('companyProfile.manual.images.title')}</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="mobile-dna-upload-trigger"
                  onClick={() => imageInputRef.current?.click()}
                >
                  <img src={iconUpload} alt="" />
                  <span>{t('companyProfile.manual.images.uploadCta')}</span>
                </button>
                {uploadedImages.length > 0 && (
                  <div className="mobile-dna-image-grid">
                    {uploadedImages.map((img) => (
                      <div className="mobile-dna-image-thumb" key={img.id || img.url || img.name}>
                        <img src={img.preview || img.url} alt={img.name || 'image'} />
                        <button
                          type="button"
                          className="mobile-dna-icon-button danger"
                          onClick={() => removeFile('images', img.id)}
                        >
                          <img src={iconDelete} alt="" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mobile-dna-upload-card">
                <div className="mobile-dna-upload-head">
                  <div className="mobile-dna-upload-title">
                    <img src={iconPdf} alt="" />
                    <span>{t('companyProfile.manual.documents.title')}</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="mobile-dna-upload-trigger"
                  onClick={() => documentInputRef.current?.click()}
                >
                  <img src={iconUpload} alt="" />
                  <span>{t('companyProfile.manual.documents.uploadCta')}</span>
                </button>
                {uploadedDocuments.length > 0 && (
                  <div className="mobile-dna-resource-grid">
                    {uploadedDocuments.map((doc) => (
                      <div className="mobile-dna-resource" key={doc.id || doc.url || doc.name}>
                        <div className="mobile-dna-resource-icon">
                          <img src={iconPdf} alt="" />
                        </div>
                        <div className="mobile-dna-resource-info">
                          <p className="mobile-dna-resource-name">{doc.name}</p>
                          <p className="mobile-dna-resource-meta">{doc.size}</p>
                        </div>
                        <button
                          type="button"
                          className="mobile-dna-icon-button danger"
                          onClick={() => removeFile('documents', doc.id)}
                          aria-label={t('companyProfile.common.removeAlt')}
                        >
                          <img src={iconDelete} alt="" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mobile-dna-upload-card">
                <div className="mobile-dna-upload-head">
                  <div className="mobile-dna-upload-title">
                    <img src={iconWebsite} alt="" />
                    <span>{t('companyProfile.manual.links.title')}</span>
                  </div>
                </div>
                <div className="mobile-dna-link-input">
                  <input
                    type="url"
                    value={linkTextarea}
                    onChange={(e) => setLinkTextarea(e.target.value)}
                    placeholder={t('companyProfile.manual.links.placeholder')}
                    className="mobile-dna-input"
                  />
                  <button
                    type="button"
                    className="mobile-dna-button surface"
                    onClick={handleAddLinks}
                  >
                    {t('companyProfile.manual.links.addCta', { defaultValue: 'Aggiungi link' })}
                  </button>
                </div>
                {linkError && (
                  <div className="mobile-error-display">
                    <span>{linkError}</span>
                  </div>
                )}
                {uploadedLinks.length > 0 && (
                  <div className="mobile-dna-resource-grid">
                    {uploadedLinks.map((link) => (
                      <div className="mobile-dna-resource" key={link.id || link.url}>
                        <div className="mobile-dna-resource-icon">
                          <img src={iconWebsite} alt="" />
                        </div>
                        <div className="mobile-dna-resource-info">
                          <p className="mobile-dna-resource-name">
                            {link.description || link.title || link.url}
                          </p>
                          <p className="mobile-dna-resource-meta">{link.url}</p>
                        </div>
                        <button
                          type="button"
                          className="mobile-dna-icon-button danger"
                          onClick={() => removeFile('links', link.id)}
                          aria-label={t('companyProfile.common.removeAlt')}
                        >
                          <img src={iconDelete} alt="" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="mobile-dna-ai-cta">
              <div className="mobile-dna-ai-copy">
                <p className="mobile-dna-ai-label">{t('companyProfile.ai.header')}</p>
                <p className="mobile-dna-ai-hint">{t('companyProfile.ai.infoNote')}</p>
              </div>
              <button
                type="button"
                className="mobile-dna-button primary"
                onClick={() => setShowDnaConfirmModal(true)}
                disabled={isGenerating || isMobileProcessing}
              >
                <img src={iconAi} alt="" />
                {isGenerating ? t('companyProfile.manual.buttons.generating') : t('companyProfile.ai.actions.start')}
              </button>
            </div>
          </section>
        )}

        {isProfileStep && (
          <>
            <div className="mobile-dna-panel-row">
              <section className="mobile-dna-card">
                <div className="mobile-dna-card-header">
                  <p className="mobile-dna-eyebrow">{t('companyProfile.manual.header')}</p>
                  <h2 className="mobile-dna-card-title">{t('companyProfile.manual.sections.general')}</h2>
                  <p className="mobile-dna-card-description">{t('companyProfile.manual.subtitle')}</p>
                </div>

                <div className="mobile-dna-fields-stack">
                  <LabeledField label={t('companyProfile.manual.placeholders.name')}>
                    <input
                      className="mobile-dna-input"
                      type="text"
                      value={profileData.name || ''}
                      onChange={(e) => updateProfileField('name', e.target.value)}
                    />
                  </LabeledField>

                  <LabeledField label={t('companyProfile.manual.dropdownLabels.industry')}>
                    <DropdownSelect
                      value={profileData.industry || ''}
                      onChange={(value) => updateProfileField('industry', value)}
                      placeholder={t('companyProfile.manual.placeholders.industry')}
                      options={industryOptions}
                    />
                  </LabeledField>

                  <LabeledField label={t('companyProfile.manual.dropdownLabels.size')}>
                    <DropdownSelect
                      value={profileData.size || ''}
                      onChange={(value) => updateProfileField('size', value)}
                      placeholder={t('companyProfile.manual.placeholders.size')}
                      options={companySizeOptions}
                    />
                  </LabeledField>

                  <LabeledField label={t('companyProfile.manual.placeholders.founded')}>
                    <input
                      className="mobile-dna-input"
                      type="number"
                      min="1800"
                      max={new Date().getFullYear()}
                      value={profileData.founded_year || ''}
                      onChange={(e) => updateProfileField('founded_year', e.target.value)}
                    />
                  </LabeledField>

                  <LabeledField label={t('companyProfile.manual.placeholders.website')}>
                    <div className="mobile-dna-input-with-icon">
                      <img src={iconWebsite} alt="" />
                      <input
                        className="mobile-dna-input"
                        type="url"
                        value={companyWebsite}
                        onChange={(e) => updateProfileField('website', e.target.value)}
                        placeholder={t('desktop.placeholders.url', { defaultValue: 'https://' })}
                      />
                    </div>
                  </LabeledField>

                  <LabeledField label={t('companyProfile.manual.placeholders.email')}>
                    <input
                      className="mobile-dna-input"
                      type="email"
                      value={profileData.email || ''}
                      onChange={(e) => updateProfileField('email', e.target.value)}
                    />
                  </LabeledField>

                  <LabeledField label={t('companyProfile.manual.placeholders.phone')}>
                    <input
                      className="mobile-dna-input"
                      type="tel"
                      value={profileData.phone || ''}
                      onChange={(e) => updateProfileField('phone', e.target.value)}
                    />
                  </LabeledField>

                  <LabeledField label={t('companyProfile.manual.placeholders.address')}>
                    <textarea
                      className="mobile-dna-textarea"
                      rows={2}
                      value={profileData.address || ''}
                      onChange={(e) => updateProfileField('address', e.target.value)}
                    />
                  </LabeledField>
                </div>
              </section>

              <section className="mobile-dna-card">
                <div className="mobile-dna-card-header">
                  <p className="mobile-dna-eyebrow">{t('companyProfile.manual.placeholders.communication')}</p>
                  <h2 className="mobile-dna-card-title">{t('companyProfile.manual.sections.brand')}</h2>
                  <p className="mobile-dna-card-description">
                    {t('companyProfile.manual.heroDescription', { defaultValue: 'Spiega chi siete, pubblico e stile comunicativo.' })}
                  </p>
                </div>

                <div className="mobile-dna-fields-stack">
                  <LabeledField label={t('companyProfile.manual.placeholders.description')}>
                    <textarea
                      className="mobile-dna-textarea"
                      rows={3}
                      value={profileData.description || ''}
                      onChange={(e) => updateProfileField('description', e.target.value)}
                    />
                  </LabeledField>

                  <LabeledField label={t('companyProfile.manual.placeholders.target')}>
                    <textarea
                      className="mobile-dna-textarea"
                      rows={3}
                      value={profileData.target_audience || ''}
                      onChange={(e) => updateProfileField('target_audience', e.target.value)}
                    />
                  </LabeledField>

                  <LabeledField label={t('companyProfile.manual.placeholders.values')}>
                    <textarea
                      className="mobile-dna-textarea"
                      rows={2}
                      value={profileData.brand_values || ''}
                      onChange={(e) => updateProfileField('brand_values', e.target.value)}
                    />
                  </LabeledField>

                  <LabeledField label={t('companyProfile.manual.dropdownLabels.personality')}>
                    <DropdownSelect
                      value={profileData.brand_personality || ''}
                      onChange={(value) => updateProfileField('brand_personality', value)}
                      placeholder={t('companyProfile.manual.placeholders.personality')}
                      options={brandPersonalityOptions}
                    />
                  </LabeledField>

                  <LabeledField label={t('companyProfile.manual.placeholders.communication')}>
                    <textarea
                      className="mobile-dna-textarea"
                      rows={2}
                      value={profileData.communication_style || ''}
                      onChange={(e) => updateProfileField('communication_style', e.target.value)}
                    />
                  </LabeledField>

                  <LabeledField label={t('companyProfile.manual.placeholders.missionVision')}>
                    <textarea
                      className="mobile-dna-textarea"
                      rows={2}
                      value={profileData.mission_vision || ''}
                      onChange={(e) => updateProfileField('mission_vision', e.target.value)}
                    />
                  </LabeledField>

                  <LabeledField label={t('companyProfile.manual.placeholders.keyProducts')}>
                    <textarea
                      className="mobile-dna-textarea"
                      rows={2}
                      value={profileData.key_products || ''}
                      onChange={(e) => updateProfileField('key_products', e.target.value)}
                    />
                  </LabeledField>

                  <LabeledField label={t('companyProfile.manual.placeholders.advantage')}>
                    <textarea
                      className="mobile-dna-textarea"
                      rows={2}
                      value={profileData.competitive_advantage || ''}
                      onChange={(e) => updateProfileField('competitive_advantage', e.target.value)}
                    />
                  </LabeledField>

                  <LabeledField label={t('companyProfile.manual.placeholders.history')}>
                    <textarea
                      className="mobile-dna-textarea"
                      rows={2}
                      value={profileData.company_history || ''}
                      onChange={(e) => updateProfileField('company_history', e.target.value)}
                    />
                  </LabeledField>
                </div>
              </section>
            </div>

            <div className="mobile-dna-ai-cta mobile-dna-final-cta">
              <div className="mobile-dna-ai-copy">
                <p className="mobile-dna-ai-label">
                  {t('companyProfile.dna.steps.profile', { defaultValue: 'Profilo DNA' })}
                </p>
                <p className="mobile-dna-ai-hint">
                  {t('companyProfile.dna.steps.finalizeHint', { defaultValue: 'Rivedi i dati compilati e genera il DNA definitivo.' })}
                </p>
              </div>
              <button
                type="button"
                className="mobile-dna-button primary"
                onClick={handleGenerateManualDna}
                disabled={
                  isMobileProcessing ||
                  manualGenerating ||
                  validationLoading ||
                  (validationResult && validationResult.can_generate_dna === false)
                }
              >
                <img src={iconAi} alt="" />
                {manualGenerating
                  ? t('companyProfile.manual.buttons.generating')
                  : t('companyProfile.manual.buttons.generate', { defaultValue: 'Genera DNA' })}
              </button>
            </div>

            <div className="mobile-dna-validation-cta">
              <div className="mobile-dna-ai-copy">
                <p className="mobile-dna-ai-label">
                  {t('companyProfile.manual.validation.title', { defaultValue: 'Validazione input' })}
                </p>
                <p className="mobile-dna-ai-hint">
                  {t('companyProfile.manual.validation.subtitle', { defaultValue: 'Verifica completezza prima di generare il DNA.' })}
                </p>
              </div>
              <div className="mobile-dna-validation-actions">
                <button
                  type="button"
                  className="mobile-dna-button surface"
                  onClick={handleValidateManual}
                  disabled={validationLoading || manualGenerating || isMobileProcessing}
                >
                  {validationLoading
                    ? t('companyProfile.manual.buttons.validating', { defaultValue: 'Validazione...' })
                    : t('companyProfile.manual.buttons.validate', { defaultValue: 'Valida input' })}
                </button>
                <button
                  type="button"
                  className="mobile-dna-button primary"
                  onClick={handleGenerateManualDna}
                  disabled={
                    manualGenerating ||
                    isMobileProcessing ||
                    validationLoading ||
                    (validationResult && validationResult.can_generate_dna === false)
                  }
                >
                  {manualGenerating
                    ? t('companyProfile.manual.buttons.generating')
                    : t('companyProfile.manual.buttons.generate', { defaultValue: 'Genera DNA' })}
                </button>
              </div>
            </div>

            {validationError && (
              <div className="mobile-error-display" role="alert">
                <span>{validationError}</span>
              </div>
            )}

            {validationResult && (
              <div className="mobile-dna-card mobile-dna-validation-result">
                <div className="mobile-dna-validation-grid">
                  <div>
                    <p className="mobile-dna-eyebrow">
                      {t('companyProfile.manual.validation.score', { defaultValue: 'Completezza' })}
                    </p>
                    <h4>{validationResult.completeness_score ?? '-' }%</h4>
                    <p className="mobile-dna-ai-hint">{validationResult.quality_level}</p>
                  </div>
                  <div>
                    <p className="mobile-dna-eyebrow">
                      {t('companyProfile.manual.validation.missing', { defaultValue: 'Campi mancanti' })}
                    </p>
                    {validationResult.missing_fields?.length ? (
                      <ul className="mobile-dna-list">
                        {validationResult.missing_fields.map((field) => (
                          <li key={field}>{field}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mobile-dna-ai-hint">
                        {t('companyProfile.manual.validation.none', { defaultValue: 'Nessun campo mancante' })}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="mobile-dna-eyebrow">
                      {t('companyProfile.manual.validation.recommendations', { defaultValue: 'Suggerimenti' })}
                    </p>
                    {validationResult.recommendations?.length ? (
                      <ul className="mobile-dna-list">
                        {validationResult.recommendations.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mobile-dna-ai-hint">
                        {t('companyProfile.manual.validation.ready', { defaultValue: 'Pronto per la generazione' })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {isSummaryStep && (
          <section className="mobile-dna-card mobile-dna-summary-card">
            <div className="mobile-dna-card-header">
              <p className="mobile-dna-eyebrow">
                {t('companyProfile.dna.steps.complete', { defaultValue: 'DNA completo' })}
              </p>
              <h2 className="mobile-dna-card-title">
                {t('companyProfile.dna.summary.title', { defaultValue: 'Riepilogo finale' })}
              </h2>
              <p className="mobile-dna-card-description">
                {t('companyProfile.dna.summary.subtitle', { defaultValue: 'Materiali e descrizione usati per il tuo DNA aziendale.' })}
              </p>
            </div>

            {dnaLoading && <p className="mobile-dna-ai-hint">{t('common.loading')}</p>}

            <div className="mobile-dna-summary-grid">
              <div className="mobile-dna-summary-block">
                <p className="mobile-dna-eyebrow">{t('companyProfile.manual.logoSection.title')}</p>
                <div className="mobile-dna-summary-logo">
                  {normalizedCompanyLogo ? (
                    <img
                      src={normalizedCompanyLogo.preview || normalizedCompanyLogo.url}
                      alt={normalizedCompanyLogo.name || 'logo'}
                    />
                  ) : (
                    <p className="mobile-dna-empty">{t('companyProfile.manual.logoSection.placeholder')}</p>
                  )}
                </div>
              </div>
              <div className="mobile-dna-summary-block">
                <p className="mobile-dna-eyebrow">{t('companyProfile.manual.placeholders.description')}</p>
                <p className="mobile-dna-summary-text">
                  {profileData.description || t('companyProfile.dna.summary.noDescription', {
                    defaultValue: 'Non e stata fornita una descrizione aziendale.'
                  })}
                </p>
              </div>
            </div>

            <div className="mobile-dna-summary-block">
              <p className="mobile-dna-eyebrow">{t('companyProfile.manual.images.title')}</p>
              {uploadedImages.length > 0 ? (
                <div className="mobile-dna-image-grid">
                  {uploadedImages.map((file) => (
                    <div className="mobile-dna-image-thumb" key={file.id || file.url || file.name}>
                      <img src={file.preview || file.url} alt={file.name || 'image'} />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mobile-dna-empty">{t('companyProfile.manual.images.placeholder', { defaultValue: 'Nessuna immagine caricata' })}</p>
              )}
            </div>

            <div className="mobile-dna-summary-grid">
              <div className="mobile-dna-summary-block">
                <p className="mobile-dna-eyebrow">{t('companyProfile.manual.documents.title')}</p>
                {uploadedDocuments.length > 0 ? (
                  <div className="mobile-dna-resource-grid">
                    {uploadedDocuments.map((doc) => (
                      <div className="mobile-dna-resource" key={doc.id || doc.url || doc.name}>
                        <div className="mobile-dna-resource-icon">
                          <img src={iconPdf} alt="" />
                        </div>
                        <div className="mobile-dna-resource-info">
                          <p className="mobile-dna-resource-name">{doc.name}</p>
                          <p className="mobile-dna-resource-meta">{doc.size}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mobile-dna-empty">{t('companyProfile.manual.documents.placeholder', { defaultValue: 'Nessun documento caricato' })}</p>
                )}
              </div>

              <div className="mobile-dna-summary-block">
                <p className="mobile-dna-eyebrow">{t('companyProfile.manual.links.title')}</p>
                {uploadedLinks.length > 0 ? (
                  <div className="mobile-dna-resource-grid">
                    {uploadedLinks.map((link) => (
                      <div className="mobile-dna-resource" key={link.id || link.url}>
                        <div className="mobile-dna-resource-icon">
                          <img src={iconWebsite} alt="" />
                        </div>
                        <div className="mobile-dna-resource-info">
                          <p className="mobile-dna-resource-name">
                            {link.description || link.title || link.url}
                          </p>
                          <p className="mobile-dna-resource-meta">{link.url}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mobile-dna-empty">Nessun link disponibile</p>
                )}
              </div>
            </div>

            {dnaMetadata?.metadata && (
              <div className="mobile-dna-summary-block">
                <p className="mobile-dna-eyebrow">
                  {t('companyProfile.dna.summary.metadata', { defaultValue: 'Metadati analisi' })}
                </p>
                <pre className="mobile-dna-readonly">{JSON.stringify(dnaMetadata.metadata, null, 2)}</pre>
              </div>
            )}

            {dnaMetadata?.completeness && (
              <div className="mobile-dna-summary-block">
                <p className="mobile-dna-eyebrow">
                  {t('companyProfile.dna.summary.completeness', { defaultValue: 'Completezza' })}
                </p>
                <pre className="mobile-dna-readonly">{JSON.stringify(dnaMetadata.completeness, null, 2)}</pre>
              </div>
            )}

          </section>
        )}
      </div>

      <input
        ref={documentInputRef}
        type="file"
        multiple
        accept=".pdf,.doc,.docx,.ppt,.pptx"
        onChange={handleDocumentUpload}
        style={{ display: 'none' }}
      />
      <input
        ref={imageInputRef}
        type="file"
        multiple
        accept="image/*"
        onChange={handleImageUpload}
        style={{ display: 'none' }}
      />

      <DnaConfirmModal
        isOpen={showDnaConfirmModal}
        onConfirm={() => {
          setShowDnaConfirmModal(false);
          handleGenerateDNA();
        }}
        onCancel={() => setShowDnaConfirmModal(false)}
        isGenerating={isGenerating}
        translations={dnaConfirmTranslations}
      />
    </div>
  );
};

export default MobileCompanyProfileFlow;
